import { useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { formatClock, formatMoney } from '../mirror/time';
import type { DayHours } from '@yourstore/domain';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Settings — the menu, the week, and the days the shop is shut.
 *
 * The hours editor keeps `windows` as a **list** even though a spa's day is one
 * stretch, because the domain type is a list (spec §4.6: a restaurant opens
 * 11:30–14:00 and again 17:30–22:00) and an editor that only ever wrote one
 * window would silently truncate a split shift the moment the restaurant vertical
 * arrives. It is cheaper to render two rows now than to find out later that this
 * screen has been discarding somebody's evening service.
 *
 * Saving sends all seven days as one `PUT`, which is the shape `core-api`
 * insists on and for the offline reason: a week patched a day at a time by two
 * devices is a merge, and §8.3 does not merge.
 */
export function Settings(): ReactNode {
  const runtime = useRuntime();
  const session = useLive(() => runtime.mirror.session(), null);
  const currency = session?.tenant.currency ?? 'USD';
  const timezone = session?.tenant.timezone ?? 'UTC';

  return (
    <section className="view view--settings">
      <ServicesPanel currency={currency} />
      <HoursPanel />
      <BlackoutsPanel timezone={timezone} />
      <SessionPanel />
    </section>
  );
}

function ServicesPanel({ currency }: { currency: string }): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const services = useLive(() => runtime.mirror.services(), []);
  const resources = useLive(() => runtime.mirror.resources(), []);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('60');
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <article className="panel">
      <h2>Menu</h2>
      <ul className="service-list">
        {services.map((s) => (
          <li key={s.service_id} className={s.active ? '' : 'is-retired'}>
            <div>
              <strong>{s.name}</strong>
              <small>
                {s.duration_min} min · {formatMoney(s.price_cents, currency)}
                {s.buffer_before_min + s.buffer_after_min > 0
                  ? ` · +${s.buffer_before_min + s.buffer_after_min} min buffer`
                  : ''}
              </small>
              {editing === s.service_id ? (
                <ServiceEditor service={s} onDone={() => setEditing(null)} />
              ) : null}
            </div>
            <div className="row-actions">
              {s.bookable_by_partner ? null : (
                <span className="badge" title="Kept off the booking agent's menu.">
                  counter only
                </span>
              )}
              <button
                type="button"
                className="link-button"
                onClick={() => setEditing(editing === s.service_id ? null : s.service_id)}
              >
                {editing === s.service_id ? 'Close' : 'Edit'}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() =>
                  void actions.updateService({
                    serviceId: s.service_id,
                    name: s.name,
                    changes: { active: !s.active },
                  })
                }
              >
                {/*
                  Retire, not delete. There is no `DELETE /v1/services` and there
                  will not be: a service somebody was booked into last March is
                  part of the book. `active: false` takes it off the menu and
                  leaves the history intact (spec §7.2 — a switch, not a
                  deletion).
                */}
                {s.active ? 'Retire' : 'Restore'}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="editor editor--inline">
        <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <input
          placeholder="Price"
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <input
          placeholder="Minutes"
          inputMode="numeric"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />
        <button
          type="button"
          className="button"
          disabled={name.trim() === '' || price.trim() === ''}
          onClick={() => {
            void actions.createService({
              name: name.trim(),
              // Money is integer cents everywhere (dev.md), so the dollars the
              // owner types are converted exactly once, here, at the edge.
              priceCents: Math.round(Number(price) * 100),
              durationMin: Number(duration),
              resourceIds: resources.filter((r) => r.active).map((r) => r.resource_id),
            });
            setName('');
            setPrice('');
          }}
        >
          Add service
        </button>
      </div>
    </article>
  );
}

/**
 * Editing a service — name, price, length, and whether the agent may sell it.
 *
 * Only the fields that changed are sent. `UpdateServiceBody` is a partial and
 * `core-api` treats `undefined` as "leave it alone", so a form that always sent
 * every field would turn "I fixed a typo in the name" into a write that also
 * re-asserts a price somebody else edited on the other iPad five minutes ago.
 * Sending the diff is the difference between an edit and an overwrite.
 */
function ServiceEditor({
  service,
  onDone,
}: {
  service: {
    service_id: string;
    name: string;
    price_cents: number;
    duration_min: number;
    bookable_by_partner: boolean;
  };
  onDone: () => void;
}): ReactNode {
  const actions = useActions();
  const [name, setName] = useState(service.name);
  const [price, setPrice] = useState((service.price_cents / 100).toFixed(2));
  const [duration, setDuration] = useState(String(service.duration_min));
  const [partner, setPartner] = useState(service.bookable_by_partner);

  return (
    <div className="editor editor--inline">
      <input value={name} onChange={(e) => setName(e.target.value)} />
      <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
      <input inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} />
      <label className="checkbox">
        <input type="checkbox" checked={partner} onChange={(e) => setPartner(e.target.checked)} />
        <span>the agent may book it</span>
      </label>
      <button
        type="button"
        className="button"
        onClick={() => {
          const priceCents = Math.round(Number(price) * 100);
          const changes: Record<string, unknown> = {};
          if (name !== service.name) changes['name'] = name;
          if (priceCents !== service.price_cents) changes['price_cents'] = priceCents;
          if (Number(duration) !== service.duration_min) {
            changes['duration_min'] = Number(duration);
          }
          if (partner !== service.bookable_by_partner) changes['bookable_by_partner'] = partner;
          if (Object.keys(changes).length > 0) {
            void actions.updateService({
              serviceId: service.service_id,
              name: service.name,
              changes,
            });
          }
          onDone();
        }}
      >
        Save
      </button>
    </div>
  );
}

function HoursPanel(): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const stored = useLive(() => runtime.mirror.hours(), null);
  const [draft, setDraft] = useState<DayHours[] | null>(null);

  const week = draft ?? stored?.hours ?? emptyWeek();

  const change = (weekday: number, apply: (day: DayHours) => DayHours): void => {
    setDraft(week.map((d) => (d.weekday === weekday ? apply(d) : d)));
  };

  return (
    <article className="panel">
      <h2>Opening hours</h2>
      <ul className="hours-list">
        {week.map((day) => (
          <li key={day.weekday}>
            <span className="hours-list__name">{DAY_NAMES[day.weekday]}</span>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={day.closed}
                onChange={(e) => change(day.weekday, (d) => ({ ...d, closed: e.target.checked }))}
              />
              <span>closed</span>
            </label>
            {day.closed
              ? null
              : day.windows.map((window, index) => (
                  <span key={index} className="hours-list__window">
                    <input
                      type="time"
                      value={window.start}
                      onChange={(e) =>
                        change(day.weekday, (d) => ({
                          ...d,
                          windows: d.windows.map((w, i) =>
                            i === index ? { ...w, start: e.target.value } : w,
                          ),
                        }))
                      }
                    />
                    <input
                      type="time"
                      value={window.end}
                      onChange={(e) =>
                        change(day.weekday, (d) => ({
                          ...d,
                          windows: d.windows.map((w, i) =>
                            i === index ? { ...w, end: e.target.value } : w,
                          ),
                        }))
                      }
                    />
                    <button
                      type="button"
                      className="link-button link-button--danger"
                      onClick={() =>
                        change(day.weekday, (d) => ({
                          ...d,
                          windows: d.windows.filter((_, i) => i !== index),
                        }))
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
            {day.closed ? null : (
              <button
                type="button"
                className="link-button"
                onClick={() =>
                  change(day.weekday, (d) => ({
                    ...d,
                    windows: [...d.windows, { start: '09:00', end: '17:00' }],
                  }))
                }
              >
                + window
              </button>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="button button--primary"
        disabled={draft === null}
        onClick={() => {
          void actions.setHours(week);
          setDraft(null);
        }}
      >
        Save the week
      </button>
    </article>
  );
}

function BlackoutsPanel({ timezone }: { timezone: string }): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const blackouts = useLive(() => runtime.mirror.blackouts(), []);
  const [reason, setReason] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  return (
    <article className="panel">
      <h2>Closures</h2>
      <ul className="blackout-list">
        {blackouts.map((b) => (
          <li key={b.blackout_id}>
            <span>
              {formatClock(b.start, timezone)} – {formatClock(b.end, timezone)}
              {b.reason === null ? '' : ` · ${b.reason}`}
            </span>
            <button
              type="button"
              className="link-button link-button--danger"
              onClick={() =>
                void actions.deleteBlackout({ blackoutId: b.blackout_id, reason: b.reason })
              }
            >
              Remove
            </button>
          </li>
        ))}
        {blackouts.length === 0 ? <li className="muted">Nothing closed.</li> : null}
      </ul>
      <div className="editor editor--inline">
        <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        <input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button
          type="button"
          className="button"
          disabled={start === '' || end === ''}
          onClick={() => {
            void actions.createBlackout({
              startIso: new Date(start).toISOString(),
              endIso: new Date(end).toISOString(),
              reason: reason.trim() === '' ? undefined : reason.trim(),
            });
            setStart('');
            setEnd('');
            setReason('');
          }}
        >
          Close this time
        </button>
      </div>
    </article>
  );
}

/**
 * What this device is, and what it may do.
 *
 * `modules` and `can_write` come from `GET /v1/session`, which is a
 * **projection** of the tenant's entitlements crossed with the mounted route
 * table — never a list this app maintains. Showing it is how an owner finds out
 * why a button is missing without anyone having to guess.
 */
function SessionPanel(): ReactNode {
  const runtime = useRuntime();
  const session = useLive(() => runtime.mirror.session(), null);
  if (session === null) return null;

  return (
    <article className="panel">
      <h2>This device</h2>
      <dl className="facts">
        <dt>Business</dt>
        <dd>
          {session.tenant.name} ({session.tenant.tenant_id})
        </dd>
        <dt>Timezone</dt>
        <dd>{session.tenant.timezone}</dd>
        <dt>Status</dt>
        <dd>{session.tenant.status}</dd>
        <dt>Signed in as</dt>
        <dd>
          {session.user.uid} · {session.user.role}
        </dd>
        <dt>Switched on</dt>
        <dd>{session.modules.join(', ')}</dd>
        <dt>May change the book</dt>
        <dd>{session.can_write ? 'yes' : 'no — this login can read only'}</dd>
      </dl>
    </article>
  );
}

function emptyWeek(): DayHours[] {
  return Array.from({ length: 7 }, (_, weekday) => ({ weekday, closed: true, windows: [] }));
}
